import { Grid, Box } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  IJobCloseReasonDTO,
  updateJobCloseReasons,
  saveJobCloseReasons,
  getJobCloseReasons,
} from 'wisestep-api';
import { WsTypography, WsTypoVariants, Label, WsButton, ControlledTextInput } from 'wisestep-base';

import { useUserContext, useWorkspaceContext } from '../../../context';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../../features/teams/helper/defaultValues';
import { AtsCustomAPIAccess, useAtsCustomAPIAccess } from '../../../hooks/useAtsCustomAPIAccess';
import { success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { HeaderBox } from '../components/HeaderBox';
import JobSettingsLayout from '../components/JobSettingsLayout';

const JobClosing = () => {
  const methods = useForm();
  const {
    handleSubmit,
    setValue,
    formState: { isSubmitSuccessful },
  } = methods;
  useEffect(() => {
    setValue('reason', '');
  }, [isSubmitSuccessful, setValue]);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const workspaceContext = useWorkspaceContext();
  const { access: atsCustomAccessControl } = useAtsCustomAPIAccess([
    AtsCustomAPIAccess.MANAGE_REASON_FOR_CLOSING_JOB_SETTING,
  ]);

  const manageAccess = workspaceContext.owns || atsCustomAccessControl[0];

  const loggedInUser = useUserContext();
  const defaultSettings: IJobCloseReasonDTO = {
    id: '',
    reasons: [
      'Role has been filled',
      'Role is no longer necessary',
      'Role requirement has changed',
      'Other reason',
    ],
    createdbyid: loggedInUser?.workspaceUser?.id ?? '',
    createdon: '',
    modifiedbyid: loggedInUser?.workspaceUser?.id ?? '',
    modifiedon: '',
  };
  const [reasonData, setReasonData] = useState<IJobCloseReasonDTO>(defaultSettings);
  const [state, setState] = useState<'new' | 'old'>('old');
  const [newReason, setNewReason] = useState('');
  useEffect(() => {
    (async function () {
      const getJobCloseReasonsReq = await getJobCloseReasons();
      const getJobCloseReasonsRes: IJobCloseReasonDTO = getJobCloseReasonsReq.data;
      if (!isEmpty(getJobCloseReasonsRes.id)) {
        if (!getJobCloseReasonsRes.reasons?.includes('Other reason')) {
          getJobCloseReasonsRes.reasons.push('Other reason');
        }
        setReasonData(getJobCloseReasonsRes);
      } else setState('new');
    })();
  }, []);
  const onRemove = async (reason: string) => {
    if (reasonData) {
      const tempData: IJobCloseReasonDTO = reasonData;
      const idx = tempData?.reasons?.indexOf(reason);
      if (idx != null) {
        tempData?.reasons?.splice(idx, 1);
      }
      setReasonData({ ...tempData });
      try {
        state === 'old'
          ? await updateJobCloseReasons({ ...tempData })
          : await saveJobCloseReasons({ ...tempData });
        setNotification(success('Job close reasons Updated Successfully.'));
      } catch (error) {
        setNotification(warning('Something Went Wrong , Please try again'));
      }
    }
  };

  const onSubmit = async () => {
    if (reasonData && newReason) {
      const tempData: IJobCloseReasonDTO = reasonData;
      const idx = tempData?.reasons?.indexOf(newReason);
      const reasonsLength = tempData?.reasons?.length;
      if (idx != null) {
        if (idx === -1) {
          tempData?.reasons?.splice(reasonsLength - 1, 0, newReason);
        } else {
          tempData?.reasons?.splice(idx, 1);
        }
      }
      setReasonData({ ...tempData });
      try {
        state === 'old'
          ? await updateJobCloseReasons({ ...tempData })
          : await saveJobCloseReasons({ ...tempData });
        setNotification(success('Job close reasons Updated Successfully.'));
        setNewReason('');
      } catch (error) {
        setNotification(warning('Something Went Wrong , Please try again'));
      }
    }
  };
  return (
    <JobSettingsLayout
      title="Reasons for Closing a Job"
      indexLabel="Reasons for Closing a Job"
      indexUrl="/close-job-reason"
      breadCrumbsList={getSettingBreadCrumbList(ISettingBreadCrumbTypes.JOBS, Pages['CLOSE_JOB'])}
    >
      <Box component="form" px={8} pt={2}>
        <HeaderBox mb={5}>
          <WsTypography variant={WsTypoVariants.SUB_TITLE}>
            Customize Your Reasons for Closing a Job
          </WsTypography>
        </HeaderBox>

        <Grid container direction="column" rowSpacing={2} xs={8} mb={2}>
          {reasonData &&
            reasonData?.reasons.map((jobClosingreason) => (
              <Grid item display="flex" justifyContent="space-between" key={jobClosingreason}>
                <Label sx={{ fontWeight: 'bold' }}>{jobClosingreason}</Label>
                {manageAccess && jobClosingreason !== 'Other reason' && (
                  <WsButton onClick={() => onRemove(jobClosingreason)} variant="text">
                    remove
                  </WsButton>
                )}
              </Grid>
            ))}
          <Grid item mb={2} mt={4}>
            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
              Add New Reason for Closing a Job
            </WsTypography>
          </Grid>

          <Grid item>
            <Grid container direction="column" xs={12} mt={2}>
              <Grid
                item
                xs={8}
                mb={1}
                spacing={2}
                display="flex"
                direction="column"
                justifyContent="space-between"
              >
                <FormProvider {...methods}>
                  <ControlledTextInput
                    name="reason"
                    label="Add new Reason"
                    value={newReason}
                    onChange={(newReasonInput) => setNewReason(newReasonInput.target.value)}
                    sx={{ mb: '1vw' }}
                    size="small"
                  />
                  {manageAccess && (
                    <WsButton size="small" type={'submit'} onClick={handleSubmit(onSubmit)}>
                      Add
                    </WsButton>
                  )}
                </FormProvider>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </JobSettingsLayout>
  );
};

export default JobClosing;
